import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "@/components/LanguageSelector";
import { track } from "@/lib/analytics";
import { trackProductEvent } from "@/lib/productEvents";
// Brand wordmark lives IN the repo (src/assets/brand/) so the production
// build is portable. The previous import pointed at Replit's out-of-tree
// `attached_assets/Screenshot_….png`, which exists only on the founder's
// Replit workspace — any other machine (CI, a fresh clone, a sandbox)
// failed the build on a missing file. The current PNG is a labeled
// placeholder wordmark; drop the real logo over it 1:1 to rebrand.
import northlightsLogo from "@/assets/brand/northlights-logo.png";

// Hrefs + test-id slugs stay hardcoded so analytics/QA selectors don't
// shift across locales. Labels resolve from i18n at render time so the
// footer re-renders when the user toggles languages.
type NavKey = "howItWorks" | "experience" | "features" | "pricing" | "faq";
const NAV_LINKS: { key: NavKey; href: string; testid: string }[] = [
  { key: "howItWorks", href: "#how-it-works", testid: "footer-link-how-it-works" },
  { key: "experience", href: "#experience", testid: "footer-link-experience" },
  { key: "features", href: "#features", testid: "footer-link-features" },
  { key: "pricing", href: "#pricing", testid: "footer-link-pricing" },
  { key: "faq", href: "#faq", testid: "footer-link-faq" },
];

type ExploreKey =
  | "howItWorks"
  | "scavenger"
  | "eve"
  | "indoor"
  | "santaClues"
  | "missionApp"
  | "familyApp"
  | "photoChallenge"
  | "elfAlt"
  | "grandparents";
const EXPLORE_LINKS: { key: ExploreKey; href: string }[] = [
  { key: "howItWorks", href: "/how-it-works" },
  { key: "scavenger", href: "/family-scavenger-hunt" },
  { key: "eve", href: "/christmas-eve-family-activity" },
  { key: "indoor", href: "/indoor-christmas-activity" },
  { key: "santaClues", href: "/santa-clues-for-kids" },
  { key: "missionApp", href: "/christmas-mission-app" },
  { key: "familyApp", href: "/family-christmas-app" },
  { key: "photoChallenge", href: "/christmas-photo-challenge" },
  { key: "elfAlt", href: "/elf-on-the-shelf-alternative" },
  { key: "grandparents", href: "/christmas-activities-for-grandparents-and-kids" },
];

export function Footer() {
  const { t } = useTranslation();
  // Defense-in-depth: every page that mounts <Footer/> (MarketingPage,
  // ChristmasMissionPage, ChristmasEvePage, ThankYouPage, SeoPageLayout)
  // already returns null in native via `if (__NATIVE_BUILD__) return null;`,
  // but if a future surface mounts the marketing footer we still hide it
  // in native — every link here points to a marketing/SEO/pricing route
  // that the native binary doesn't ship. Task #440.
  if (__NATIVE_BUILD__) return null;
  return (
    <footer
      className="relative border-t border-white/10 bg-[hsl(160,60%,7%)]/95 px-5 pb-10 pt-16 sm:px-8 lg:px-12"
      data-testid="section-footer"
    >
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]">
        <div>
          <Link
            href="/"
            className="inline-flex items-center gap-2 font-serif text-3xl font-semibold tracking-tight text-jingle-snow"
          >
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-jingle-cranberry shadow-[0_0_14px_hsl(0,80%,60%)]" />
            JINGLE
          </Link>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-jingle-snow/65">
            {t("marketing.footer.tagline")}
          </p>

          <p
            className="mt-6 text-xs text-jingle-snow/55"
            data-testid="text-mobile-coming-soon"
          >
            {t("marketing.footer.mobileSoon")}
          </p>

          <a
            href="https://www.facebook.com/1063371763529556"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              track("facebook_click", { source: "footer" });
              track("social_facebook_click", { source: "footer" });
            }}
            className="mt-5 inline-flex min-h-11 items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-2 text-sm text-jingle-snow/75 transition hover:border-white/25 hover:bg-white/[0.06] hover:text-jingle-snow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jingle-gold/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(160,60%,7%)]"
            aria-label={t("marketing.footer.facebookAria")}
            data-testid="footer-link-facebook"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              aria-hidden="true"
              focusable="false"
              className="shrink-0"
            >
              <path
                fill="#1877F2"
                d="M24 12a12 12 0 1 0-13.875 11.854V15.469H7.078V12h3.047V9.356c0-3.007 1.792-4.668 4.533-4.668 1.313 0 2.686.234 2.686.234v2.953h-1.513c-1.491 0-1.956.926-1.956 1.875V12h3.328l-.532 3.469h-2.796v8.385A12.003 12.003 0 0 0 24 12Z"
              />
            </svg>
            <span>{t("marketing.footer.facebook")}</span>
          </a>
        </div>

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.26em] text-jingle-gold">
            {t("marketing.footer.homepage")}
          </p>
          <ul className="mt-4 space-y-2.5 text-sm text-jingle-snow/75">
            <li>
              <Link
                href="/"
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-home"
              >
                {t("marketing.footer.home")}
              </Link>
            </li>
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  className="transition hover:text-jingle-snow"
                  data-testid={l.testid}
                >
                  {t(`marketing.nav.${l.key}`)}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.26em] text-jingle-gold">
            {t("marketing.footer.explore")}
          </p>
          <ul className="mt-4 space-y-2.5 text-sm text-jingle-snow/75">
            {EXPLORE_LINKS.map((l) => (
              <li key={l.href}>
                <Link
                  href={l.href}
                  className="transition hover:text-jingle-snow"
                  data-testid={`footer-seo-link-${l.href.replace(/^\//, "")}`}
                >
                  {t(`marketing.footer.exploreLinks.${l.key}`)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.26em] text-jingle-gold">
            {t("marketing.footer.family")}
          </p>
          <ul className="mt-4 space-y-2.5 text-sm text-jingle-snow/75">
            {/* Primary: Get launch updates */}
            <li>
              <Link
                href="/create"
                className="font-medium text-jingle-gold/90 transition hover:text-jingle-gold"
                data-testid="footer-link-launch-updates"
                onClick={() => {
                  track("hero_signup_click", { source: "footer" });
                }}
              >
                {t("marketing.footer.launchUpdates")}
              </Link>
            </li>
            <li>
              <Link
                href="/join"
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-join"
              >
                {t("marketing.footer.join")}
              </Link>
            </li>
            <li>
              <Link
                href="/login"
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-signin"
              >
                {t("marketing.footer.signin")}
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.26em] text-jingle-gold">
            {t("marketing.footer.helpLegal")}
          </p>
          <ul className="mt-4 space-y-2.5 text-sm text-jingle-snow/75">
            <li>
              <Link
                href="/about"
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-about"
              >
                {t("marketing.footer.about")}
              </Link>
            </li>
            <li>
              <a
                href="mailto:hello@northlights.ca"
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-email"
              >
                hello@northlights.ca
              </a>
            </li>
            <li>
              <Link
                href="/contact"
                onClick={() => {
                  track("contact_click", { source: "footer" });
                }}
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-contact"
              >
                {t("marketing.footer.contact")}
              </Link>
            </li>
            <li>
              <Link
                href="/privacy"
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-privacy"
              >
                {t("marketing.footer.privacy")}
              </Link>
            </li>
            <li>
              <Link
                href="/terms"
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-terms"
              >
                {t("marketing.footer.terms")}
              </Link>
            </li>
            <li>
              <Link
                href="/faq"
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-faq"
              >
                {t("marketing.footer.faq")}
              </Link>
            </li>
            <li>
              <Link
                href="/pricing"
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-pricing"
              >
                {t("marketing.footer.pricing")}
              </Link>
            </li>
            <li>
              <Link
                href="/data-deletion"
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-data-deletion"
              >
                {t("marketing.footer.dataDeletion")}
              </Link>
            </li>
            <li>
              <Link
                href="/account-deletion"
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-account-deletion"
              >
                {t("marketing.footer.accountDeletion")}
              </Link>
            </li>
            <li>
              <Link
                href="/ai-disclosure"
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-ai-disclosure"
              >
                {t("marketing.footer.aiDisclosure")}
              </Link>
            </li>
            <li>
              <Link
                href="/refund-policy"
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-refund-policy"
              >
                {t("marketing.footer.refundPolicy")}
              </Link>
            </li>
            <li>
              <Link
                href="/children-family-safety"
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-children-family-safety"
              >
                {t("marketing.footer.childrenFamilySafety")}
              </Link>
            </li>
            <li>
              <Link
                href="/trust"
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-trust"
              >
                {t("marketing.footer.trust")}
              </Link>
            </li>
            <li>
              <Link
                href="/support"
                className="transition hover:text-jingle-snow"
                data-testid="footer-link-support"
              >
                {t("marketing.footer.support")}
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="mx-auto mt-14 flex max-w-7xl flex-col items-start justify-between gap-6 border-t border-white/10 pt-8 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3 text-xs text-jingle-snow/55">
          <span>
            {t("marketing.footer.copyright", { year: new Date().getFullYear() })}
          </span>
        </div>

        <a
          href="https://northlights.ca"
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex min-h-11 flex-col items-center justify-center gap-1 rounded-md px-2 py-1 text-jingle-snow/55 transition hover:text-jingle-snow/85 focus-visible:text-jingle-snow/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jingle-gold/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(160,60%,7%)]"
          data-testid="footer-link-northlights"
          aria-label={t("marketing.footer.northlightsAria")}
        >
          <span className="text-[10px] font-semibold uppercase tracking-[0.26em] text-jingle-gold/75 transition group-hover:text-jingle-gold/95 group-focus-visible:text-jingle-gold/95">
            {t("marketing.footer.createdBy")}
          </span>
          <img
            src={northlightsLogo}
            alt={t("marketing.footer.northlightsAlt")}
            width={57}
            height={24}
            className="h-6 w-auto select-none opacity-80 transition group-hover:opacity-100 group-focus-visible:opacity-100"
            loading="lazy"
            decoding="async"
          />
        </a>

        <div className="flex items-center gap-3">
          <LanguageSelector />
        </div>
      </div>
    </footer>
  );
}
